import React from "react"

import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { JsonTextareaField } from "./JsonTextareaField"
import { WEBSOCKET_TOOLTIPS } from "./tooltips"
import { TWIML_HTTP_METHOD_OPTIONS, type WebSocketFieldProps } from "./types"

export const TwimlWebhookFields: React.FC<WebSocketFieldProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
}) => {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h4 className="text-sm font-medium text-coval-text">
          TwiML Voice Webhook
        </h4>
        <p className="text-xs text-coval-text-muted">
          Coval sends a Twilio-shaped request but does not add an{" "}
          <code className="bg-surface-1 px-1 rounded">X-Twilio-Signature</code>.
          Use Voice Webhook Headers when the endpoint requires authentication.
        </p>
      </div>

      <div className="space-y-2">
        <FieldLabel htmlFor="voice-url" tooltip={WEBSOCKET_TOOLTIPS.voiceUrl}>
          Voice Webhook URL <span className="text-coval-danger-text">*</span>
        </FieldLabel>
        <Input
          id="voice-url"
          aria-label="Voice Webhook URL"
          placeholder="https://example.com/twilio/voice"
          value={metadata.voice_url || ""}
          onChange={(event) =>
            onMetadataUpdate({ voice_url: event.target.value })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Must return TwiML containing a bidirectional{" "}
          <code className="bg-surface-1 px-1 rounded">
            {"<Connect><Stream>"}
          </code>{" "}
          with a secure WebSocket URL.
        </p>
      </div>

      <div className="space-y-2">
        <FieldLabel
          htmlFor="voice-http-method"
          tooltip={WEBSOCKET_TOOLTIPS.voiceHttpMethod}
        >
          HTTP Method
        </FieldLabel>
        <Select
          value={metadata.voice_http_method || "POST"}
          onValueChange={(value) =>
            onMetadataUpdate({ voice_http_method: value })
          }
          disabled={disabled}
        >
          <SelectTrigger id="voice-http-method" aria-label="Voice HTTP Method">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TWIML_HTTP_METHOD_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <JsonTextareaField
        id="voice-http-headers"
        label="Voice Webhook Headers"
        ariaLabel="Voice Webhook Headers"
        placeholder={'{\n  "Authorization": "Bearer YOUR_TOKEN"\n}'}
        description="Optional HTTP headers sent to the voice webhook."
        value={metadata.voice_http_headers}
        fieldName="voice_http_headers"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={3}
        tooltip={WEBSOCKET_TOOLTIPS.voiceHttpHeaders}
      />

      <JsonTextareaField
        id="voice-form-fields"
        label="Additional Form Fields"
        ariaLabel="Additional Form Fields"
        placeholder={'{\n  "gateway_mode": "nats"\n}'}
        description="Additional parameters sent with CallSid, AccountSid, From, To, Direction, and CallStatus."
        value={metadata.voice_form_fields}
        fieldName="voice_form_fields"
        onMetadataUpdate={onMetadataUpdate}
        disabled={disabled}
        rows={4}
        tooltip={WEBSOCKET_TOOLTIPS.voiceFormFields}
      />

      <div className="space-y-2">
        <FieldLabel
          htmlFor="twilio-account-sid"
          tooltip={WEBSOCKET_TOOLTIPS.twilioAccountSid}
        >
          Twilio Account SID
        </FieldLabel>
        <Input
          id="twilio-account-sid"
          aria-label="Twilio Account SID"
          placeholder="AC00000000000000000000000000000000"
          value={metadata.twilio_account_sid || ""}
          onChange={(event) =>
            onMetadataUpdate({ twilio_account_sid: event.target.value })
          }
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <FieldLabel
            htmlFor="twilio-from-number"
            tooltip={WEBSOCKET_TOOLTIPS.twilioFromNumber}
          >
            From Number
          </FieldLabel>
          <Input
            id="twilio-from-number"
            aria-label="Twilio From Number"
            type="tel"
            placeholder="+14155550100"
            value={metadata.twilio_from_number || ""}
            onChange={(event) =>
              onMetadataUpdate({ twilio_from_number: event.target.value })
            }
            disabled={disabled}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel
            htmlFor="twilio-to-number"
            tooltip={WEBSOCKET_TOOLTIPS.twilioToNumber}
          >
            To Number
          </FieldLabel>
          <Input
            id="twilio-to-number"
            aria-label="Twilio To Number"
            type="tel"
            placeholder="+14155550101"
            value={metadata.twilio_to_number || ""}
            onChange={(event) =>
              onMetadataUpdate({ twilio_to_number: event.target.value })
            }
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}
